import { PROPERTY_STATUS } from '../../browse'
import { CLOSING_SOON_THRESHOLD_DAYS } from '../constants/propertyDefaults'
import type { PropertyDetail } from '../types/propertyDetailTypes'

// Pure view-model builders for the content sections of the property-detail
// page (gallery, header, about, amenities, highlights, location, documents).
// Figures (breakdown, right-rail financials, calculator) live in
// ./propertyFinancialBuilders.ts. No Vue reactivity — each function takes the
// parsed detail (or null) and returns what its template block renders.

export type PropertyDetailData = PropertyDetail

export const PROPERTY_PLACEHOLDER_IMAGE =
  'https://placehold.co/800x500/e9eaeb/717680?text=Property+Image'

// Some descriptions arrive with backslash-escaped percent signs / apostrophes
// (LaTeX-style "5\%", "world\'s") and stray whitespace runs. Mirrors the
// marketplace parser's sanitiser so the detail surface doesn't print API
// quirks as visible typos. Empty → ''.
export const sanitiseText = (raw: string | null | undefined): string =>
  raw?.trim().replace(/\\%/g, '%').replace(/\\'/g, "'").replace(/\s+/g, ' ') ?? ''

// Title-case label rows so lowercase API enum strings ("swimming_pool") read as
// proper names ("Swimming Pool"). Labels an admin already cased ("BBQ Area")
// are left alone.
export const titleCase = (raw: string): string =>
  /[A-Z]/.test(raw) && !raw.includes('_')
    ? raw.trim()
    : raw
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ')

const integerFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

// ── Gallery ──────────────────────────────────────────────────────────────

/** Photos in display order, cover first. Never empty — falls back to a placeholder. */
export const buildGallery = (data: PropertyDetailData | null) => {
  const images = (data?.media ?? [])
    .filter((media) => media.filePath && (media.mediaType ?? 'image').startsWith('image'))
    .sort(
      (left, right) =>
        Number(Boolean(right.isCover)) - Number(Boolean(left.isCover)) ||
        (left.displayOrder ?? 0) - (right.displayOrder ?? 0),
    )
    .map((media) => media.filePath as string)
  return { images: images.length ? images : [PROPERTY_PLACEHOLDER_IMAGE] }
}

// ── Header ───────────────────────────────────────────────────────────────

export type StatusChipTone = 'warning' | 'primary' | 'success' | 'info'

export interface StatusChip {
  label: string
  tone: StatusChipTone
  /** 'flame' is the design's Closes Soon glyph (an SVG asset, not a PiIcon). */
  icon: 'flame' | 'clock' | 'calendar' | 'verified'
}

export interface HeaderSpec {
  icon: 'expand' | 'building' | 'bed' | 'bath'
  label: string
}

const ordinal = (day: number): string => {
  const TEEN_START = 11
  const TEEN_END = 13
  const tens = day % 100
  if (tens >= TEEN_START && tens <= TEEN_END) return `${day}th`
  const suffix: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' }
  return `${day}${suffix[day % 10] ?? 'th'}`
}

/** "15th March" — the Launching Soon chip copy. */
export const formatLaunchDay = (iso: string | null | undefined): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return `${ordinal(date.getDate())} ${date.toLocaleDateString('en-GB', { month: 'long' })}`
}

const pluralDays = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`

const buildLaunchChip = (data: PropertyDetailData): StatusChip | null => {
  const launchDay = formatLaunchDay(data.investmentWindowStart)
  if (launchDay) return { label: `Launches ${launchDay}`, tone: 'success', icon: 'calendar' }
  if (data.daysToLaunch != null) {
    return {
      label: `Launches in ${pluralDays(data.daysToLaunch)}`,
      tone: 'success',
      icon: 'calendar',
    }
  }
  return null
}

const buildLiveChip = (data: PropertyDetailData): StatusChip | null => {
  if (data.daysToClose == null) return null
  if (data.daysToClose <= CLOSING_SOON_THRESHOLD_DAYS) {
    return { label: 'Closes Soon', tone: 'warning', icon: 'flame' }
  }
  // Mobile app parity: a live countdown always reads in the urgent amber.
  return { label: `Closes in ${pluralDays(data.daysToClose)}`, tone: 'warning', icon: 'flame' }
}

export const buildStatusChip = (data: PropertyDetailData | null): StatusChip | null => {
  if (!data) return null
  switch (data.propertyStatus) {
    case PROPERTY_STATUS.FULLY_FUNDED:
      return { label: 'Fully Funded', tone: 'info', icon: 'verified' }
    case PROPERTY_STATUS.LAUNCHING_SOON:
      return buildLaunchChip(data)
    case PROPERTY_STATUS.LIVE:
      return buildLiveChip(data)
    default:
      return null
  }
}

const buildSpecs = (data: PropertyDetailData | null): HeaderSpec[] => {
  if (!data) return []
  const specs: (HeaderSpec | null)[] = [
    data.builtUpAreaSqm != null
      ? { icon: 'expand', label: `${integerFormatter.format(data.builtUpAreaSqm)} sqm` }
      : null,
    data.propertyType ? { icon: 'building', label: titleCase(data.propertyType) } : null,
    data.numberOfBedrooms != null
      ? {
          icon: 'bed',
          label: `${data.numberOfBedrooms} ${data.numberOfBedrooms === 1 ? 'Bed' : 'Beds'}`,
        }
      : null,
    data.numberOfBathrooms != null
      ? {
          icon: 'bath',
          label: `${data.numberOfBathrooms} ${data.numberOfBathrooms === 1 ? 'Bath' : 'Baths'}`,
        }
      : null,
  ]
  return specs.filter((spec): spec is HeaderSpec => spec !== null)
}

/** "Palm Jumeirah, Dubai" — area (or first address line) plus city. */
export const buildLocationLabel = (data: PropertyDetailData | null): string => {
  const parts = [data?.areaName || data?.addressLine1, data?.city].filter(Boolean)
  return parts.join(', ')
}

export const buildHeader = (data: PropertyDetailData | null) => ({
  title: data?.offeringTitle || data?.propertyName || '',
  location: buildLocationLabel(data),
  statusChip: buildStatusChip(data),
  specs: buildSpecs(data),
  // The one-liner under the chips; the long description goes to About.
  shortDescription: sanitiseText(data?.propertyShortDescription ?? data?.offeringDescription),
})

// ── About + developer ────────────────────────────────────────────────────

export interface DeveloperCard {
  name: string
  /** The API has no developer logo yet — the card falls back to the initial. */
  logoUrl: string | null
  /** The API has no developer tagline yet — the card shows "-". */
  tagline: string | null
}

export const buildAbout = (data: PropertyDetailData | null) => {
  const developerName = data?.developerName?.trim()
  const developer: DeveloperCard | null = developerName
    ? { name: developerName, logoUrl: null, tagline: null }
    : null
  return {
    description: sanitiseText(data?.propertyDescription ?? data?.offeringDescription),
    developer,
  }
}

// ── Amenities / highlights ───────────────────────────────────────────────

export interface AmenityItem {
  /** Admin-configured icon URL, or an icon keyword the component maps. */
  icon: string
  label: string
}

export const buildAmenities = (data: PropertyDetailData | null): AmenityItem[] =>
  (data?.amenities ?? [])
    .filter((amenity): amenity is typeof amenity & { name: string } =>
      Boolean(amenity.name?.trim()),
    )
    .map((amenity) => ({ icon: amenity.icon ?? '', label: titleCase(amenity.name) }))

export interface HighlightItem {
  icon: string
  title: string
  description: string
}

// Placeholder rows the admin form ships with ("string") are never shown.
const PLACEHOLDER_HIGHLIGHT = 'string'

export const buildHighlights = (data: PropertyDetailData | null): HighlightItem[] => {
  const rows = (data?.highlights ?? [])
    .filter((highlight) => {
      const text = highlight.highlightText?.trim()
      return Boolean(text) && text !== PLACEHOLDER_HIGHLIGHT
    })
    .sort((left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0))
    .map((highlight) => ({
      icon: highlight.highlightIcon ?? '',
      title: sanitiseText(highlight.highlightText),
      description: sanitiseText(highlight.highlightBrief),
    }))
  // Figma closes the list with the live co-investor count.
  if (data && data.totalInvestors > 0) {
    rows.push({
      icon: 'users',
      title: `${integerFormatter.format(data.totalInvestors)} co-investors`,
      description: 'Join verified investors already committed here.',
    })
  }
  return rows
}

// ── Location ─────────────────────────────────────────────────────────────

const MAP_ZOOM = 15

export const buildLocation = (data: PropertyDetailData | null) => {
  const hasCoordinates = data?.latitude != null && data?.longitude != null
  const coordinates = hasCoordinates ? `${data?.latitude},${data?.longitude}` : null
  const searchUrl = coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${coordinates}`
    : null
  return {
    label: buildLocationLabel(data),
    // Keyless Google embed — pinned on the coordinates when we have them.
    embedUrl: coordinates
      ? `https://maps.google.com/maps?q=${coordinates}&z=${MAP_ZOOM}&output=embed`
      : null,
    mapsUrl: data?.googleMapsUrl || searchUrl,
  }
}

// ── Documents ────────────────────────────────────────────────────────────

/** Which DOCUMENTS accordion a file lists under — the same two cards investor-app draws. */
export type DocumentCategory = 'legal' | 'marketing'

export interface DocumentItem {
  id: string
  category: DocumentCategory
  title: string
  /** Empty while the document is listed but not uploaded yet — the row shows, unlinked. */
  fileUrl: string
  fileName: string
  /** "PDF", "DOCX", … — shown beside the download icon. */
  format: string
}

// Relative file paths resolve against the investor service, as investor-app does.
const FILE_BASE_URL: string = import.meta.env.VITE_INVESTOR_API_URL ?? ''

/**
 * Marketing is the closed set; everything else — a title deed, a type this
 * build has never heard of — is Legal, so no document drops out of the list.
 * Same fallback as investor-app's `isMarketingDocument`.
 */
const MARKETING_DOC_TYPES = new Set(['brochure', 'presentation', 'video_tour', 'photo_set'])

/* Sentence-case labels by type, as investor-app prints them. */
const DOC_TYPE_LABELS: Record<string, string> = {
  brochure: 'Property brochure',
  valuation_report: 'Valuation report',
  title_deed: 'Title deed',
  white_paper: 'Whitepaper',
  investment_memorandum: 'Investment memorandum',
  proforma: 'Pro forma',
  presentation: 'Presentation',
  video_tour: 'Video tour',
  photo_set: 'Photo set',
  noc: 'NOC',
  makani: 'Makani',
  rent_agreement: 'Rent agreement',
  ownership_doc: 'Ownership document',
  financial_model: 'Financial model',
  fee_disclosure: 'Fee disclosure',
  fund_flow_statement: 'Fund flow statement',
  other: 'Other document',
}

const fileFormat = (fileName: string | undefined, mimeType: string | undefined): string => {
  const extension = fileName?.split('.').pop()
  if (extension && extension !== fileName) return extension.toUpperCase()
  const subtype = mimeType?.split('/').pop()
  return subtype ? subtype.toUpperCase() : 'FILE'
}

/** The service's `documentCategory` wins; the type set answers when it is missing. */
const documentCategory = (
  raw: string | null | undefined,
  documentType: string | undefined,
): DocumentCategory => {
  const category = raw?.trim().toLowerCase()
  if (category === 'legal' || category === 'marketing') return category
  return MARKETING_DOC_TYPES.has(documentType ?? '') ? 'marketing' : 'legal'
}

const toFileUrl = (filePath: string | null | undefined): string => {
  if (!filePath) return ''
  return /^https?:\/\//.test(filePath) ? filePath : `${FILE_BASE_URL}${filePath}`
}

export const buildDocuments = (data: PropertyDetailData | null): DocumentItem[] =>
  (data?.documents ?? []).map((document, index) => ({
    id: document.id ?? document.filePath ?? `document-${index}`,
    category: documentCategory(document.documentCategory, document.documentType),
    title:
      DOC_TYPE_LABELS[document.documentType ?? ''] ??
      (document.documentTitle || titleCase(document.documentType ?? '') || 'Document'),
    fileUrl: toFileUrl(document.filePath),
    fileName: document.fileName ?? 'document',
    format: fileFormat(document.fileName, document.mimeType),
  }))

export interface DocumentGroup {
  category: DocumentCategory
  title: string
  documents: DocumentItem[]
}

// Accordion order and headings (Figma 32590:62118): Legal first.
const DOCUMENT_GROUP_TITLES: Record<DocumentCategory, string> = {
  legal: 'Legal Documents',
  marketing: 'Marketing Documents',
}
const DOCUMENT_GROUP_ORDER: DocumentCategory[] = ['legal', 'marketing']

/** One accordion per category that has files, in the design's order. */
export const groupDocuments = (documents: DocumentItem[]): DocumentGroup[] =>
  DOCUMENT_GROUP_ORDER.map((category) => ({
    category,
    title: DOCUMENT_GROUP_TITLES[category],
    documents: documents.filter((document) => document.category === category),
  })).filter((group) => group.documents.length > 0)
